// The error code alone can't say which input is wrong (bookings paused and a
// too-far town share failed-precondition), so the server names it in
// details.reason. Anything unnamed — including older servers — is a retry.
const QUOTE_ERROR_FIELDS = {
  pickup_outside_area: 'pickup',
  destination_out_of_range: 'destination',
  destination_too_close: 'destination',
};

export function quoteErrorField(error) {
  return QUOTE_ERROR_FIELDS[error?.details?.reason] || null;
}

export const ACTIONABLE_STEPS = new Set([
  'choose_pickup', 'choose_destination', 'change_pickup', 'change_destination', 'retry', 'request',
]);

/**
 * The one next thing the Intertown booking bar asks the passenger to do.
 * Pickup always comes first so the destination map can check distance from it.
 */
export function intertownBookingStep({
  hasActiveRide = false,
  submitting = false,
  isCustom = false,
  hasPickup = false,
  hasDestination = false,
  resolvingDestination = false,
  loadingQuote = false,
  hasQuote = false,
  errorField = null,
} = {}) {
  if (hasActiveRide) return 'finish_ride';
  if (submitting) return 'submitting';
  if (!hasPickup) return 'choose_pickup';
  if (isCustom && !hasDestination) return 'choose_destination';
  if (resolvingDestination || loadingQuote) return 'calculating';
  if (hasDestination && hasQuote) return 'request';
  if (errorField === 'pickup') return 'change_pickup';
  if (errorField === 'destination' && isCustom) return 'change_destination';
  return 'retry';
}

export function customMaxDistanceKm(customConfig) {
  const max = Number(customConfig?.maxDistanceKm);
  return Number.isFinite(max) && max > 0 ? max : null;
}

const explicitNumber = (value) => (value === null || value === undefined || value === '' ? NaN : Number(value));

/**
 * Same centre + radius the server's assertPickupWithinIntertownRadius uses
 * (config/platform mapCenterLat/Lng, intertown.pickupRadiusKm clamped 1–50,
 * legacy custom.pickupRadiusKm fallback). Null when either isn't explicitly
 * set: the server's built-in defaults are left to the server rather than
 * copied here, so the two can never disagree and wrongly block a pickup.
 */
export function intertownPickupLimit(platform) {
  const lat = explicitNumber(platform?.mapCenterLat);
  const lng = explicitNumber(platform?.mapCenterLng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const clampRadius = (value) => {
    const radius = explicitNumber(value);
    return Number.isFinite(radius) ? Math.min(50, Math.max(1, radius)) : null;
  };
  const radiusKm = clampRadius(platform?.intertown?.pickupRadiusKm) ?? clampRadius(platform?.intertown?.custom?.pickupRadiusKm);
  return radiusKm === null ? null : { center: { lat, lng }, radiusKm };
}

/**
 * What "Book this route again" may still do under today's settings, or null when that exact
 * trip can no longer be booked: its fixed route is gone or switched off, custom trips are
 * switched off, or its trip type (one way / return) is. The trip type is never quietly swapped
 * for the other one: a return fare is not a one-way fare. `rebook` is what the receipt sent
 * (`intertownRebook`); `routes` are the enabled routes. The pickup is passed through as it is:
 * the server's quote refuses one that is out of area, and the booking screen already handles that.
 * A popular route's own drop spot (`exactDrop`) is repeated only while custom map destinations
 * are on: with them off, riders cannot pick a spot, so the route's town is used.
 */
export function intertownRebookPlan(rebook, { routes = [], customEnabled = false, oneWayEnabled = false, returnEnabled = false } = {}) {
  if (!rebook || typeof rebook !== 'object') return null;
  const tripTypeEnabled = rebook.tripType === 'one_way' ? oneWayEnabled : rebook.tripType === 'return' ? returnEnabled : false;
  if (!tripTypeEnabled) return null;
  const pickup = rebook.pickup || null;
  if (rebook.kind === 'custom') {
    return customEnabled && rebook.destination
      ? { mode: 'custom', tripType: rebook.tripType, pickup, destination: rebook.destination }
      : null;
  }
  if (rebook.kind === 'fixed' && routes.some((route) => route?.id === rebook.routeId)) {
    return {
      mode: 'fixed', routeId: rebook.routeId, tripType: rebook.tripType, pickup,
      ...(customEnabled && rebook.destination ? { exactDrop: rebook.destination } : {}),
    };
  }
  return null;
}
